import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress,
  Divider, Chip, TextField, Button, Stack
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import KitchenIcon from '@mui/icons-material/Kitchen';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getKpis } from '../../services/api';

const KpiCard = ({ title, value, subtitle, icon, color }) => (
  <Card elevation={3} sx={{ borderLeft: `5px solid ${color}`, height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h4" fontWeight={700} mt={0.5}>{value ?? '—'}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.8, fontSize: 40 }}>{icon}</Box>
      </Stack>
    </CardContent>
  </Card>
);

const FILTERS = ['This Month', 'Last Month', 'Custom'];

export default function KpiDashboard() {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('This Month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customLoading, setCustomLoading] = useState(false);

  const fetchKpis = useCallback(async (from = null, to = null) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to)   params.to   = to;
      const res = await getKpis(params);
      setKpi(res.data);
    } catch {
      console.error('Failed to fetch KPIs');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const handleCustomSearch = async () => {
    if (!fromDate || !toDate) return;
    setCustomLoading(true);
    const from = new Date(fromDate).getTime();
    const to   = new Date(toDate).getTime();
    await fetchKpis(from, to);
    setCustomLoading(false);
  };

  const bookingValue = () => {
    if (!kpi) return '—';
    if (activeFilter === 'This Month') return kpi.rideBookings.thisMonth;
    if (activeFilter === 'Last Month') return kpi.rideBookings.lastMonth;
    if (activeFilter === 'Custom')     return kpi.rideBookings.custom ?? '—';
  };

  const bookingSubtitle = () => {
    if (activeFilter === 'This Month') return 'Current month bookings';
    if (activeFilter === 'Last Month') return 'Previous month bookings';
    return fromDate && toDate ? `${fromDate} → ${toDate}` : 'Select date range';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* ── User KPIs ─────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={600} mb={1}>Users</Typography>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Total Users"
            value={kpi?.totalUsers}
            subtitle="All registered users"
            icon={<PeopleIcon fontSize="inherit" />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Active This Month"
            value={kpi?.activeUsersThisMonth}
            subtitle="Logged in this month"
            icon={<TrendingUpIcon fontSize="inherit" />}
            color="#2e7d32"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── Ride Booking KPIs ─────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1} flexWrap="wrap" gap={1}>
        <Typography variant="subtitle1" fontWeight={600}>Ride Bookings</Typography>
        <Stack direction="row" spacing={1}>
          {FILTERS.map(f => (
            <Chip
              key={f}
              label={f}
              size="small"
              color={activeFilter === f ? 'primary' : 'default'}
              variant={activeFilter === f ? 'filled' : 'outlined'}
              onClick={() => setActiveFilter(f)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Stack>

      {/* Custom date range */}
      {activeFilter === 'Custom' && (
        <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleCustomSearch}
            disabled={!fromDate || !toDate || customLoading}
          >
            {customLoading ? 'Loading...' : 'Search'}
          </Button>
        </Stack>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Ride Bookings"
            value={bookingValue()}
            subtitle={bookingSubtitle()}
            icon={<DirectionsCarIcon fontSize="inherit" />}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── Feature Usage KPIs ────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Feature Usage — Enabled Users
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Sugar Log"
            value={kpi?.featureUsage?.sugarLog}
            subtitle="Users with Sugar Log enabled"
            icon={<MonitorHeartIcon fontSize="inherit" />}
            color="#d32f2f"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Kitchen Diary"
            value={kpi?.featureUsage?.kitchenLog}
            subtitle="Users with Kitchen Diary enabled"
            icon={<KitchenIcon fontSize="inherit" />}
            color="#7b1fa2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Cycle Tracker"
            value={kpi?.featureUsage?.cycleLog}
            subtitle="Users with Cycle Tracker enabled"
            icon={<PedalBikeIcon fontSize="inherit" />}
            color="#0288d1"
          />
        </Grid>
      </Grid>
    </Box>
  );
}